import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';

import {reverse} from 'utils/urls';
import {BOOKING_DETAIL_URL} from 'config/urls';

import Loading from 'components/Loading';
import Page from 'components/Page';
import {Container} from 'containers/types';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import ListContent from 'components/ListContent';
import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';

import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {composeActions} from 'ducks/common';

import {
  BOOKING_STATUSES,
  fetchBookingsForBookingTracker,
  selectBookingsForBookingTracker,
  selectBookingsForBookingTrackerLoading,
  fetchBookingTypes,
  selectBookingTypes,
  selectBookingsFilters,
  fetchBookingListFilters
} from 'ducks/bookings';
import {
  fetchFloors,
  selectFloors,
  fetchAllRooms,
  selectAllRooms
} from 'ducks/rooms';

import {
  SubNavigation,
  BookingsTable,
  BookingsTrackerFilters
} from './components';

import {
  withCursorPagination,
  PAGINATION,
  WithCursorPaginationProps
} from 'components/withCursorPagination';

import {MODALS, openModal, isModalOpen} from 'ducks/modals';
import BookingNotesModal from 'components/BookingNotesModal';
import VisibleIf from 'components/VisibleIf';

import './styles.css';

const initialFilters: {
  roomTypes: [];
  bookingStatuses: [];
  bookingTypes: [];
  rooms: [];
  floors: [];
  arrivalStart: string;
  arrivalEnd: string;
  departureStart: string;
  departureEnd: string;
} = {
  roomTypes: [],
  bookingStatuses: [],
  bookingTypes: [],
  rooms: [],
  floors: [],
  arrivalStart: '',
  arrivalEnd: '',
  departureStart: '',
  departureEnd: ''
};

interface BookingsForBookingTrackerProps {
  id: number;
  primaryTenant: {fullName: string};
  secondaryTenant: {fullName: string};
  guarantor: {fullName: string};
  status: string;
  bookingType: string;
  periodStartDate: string;
  periodEndDate: string;
  roomTypes: string;
  room: string;
  createBy: string;
  affordabilityPassed: boolean;
  kycsPassed: boolean;
  hasCompletedAgreement: boolean;
}

interface RoomsProps {
  id: number;
  roomNumber: string;
}

interface BookingTypesProps {
  id: number;
  name: string;
  default: boolean;
  enableOrganisation: boolean;
}

interface BookingStatusFilterProps {
  name: string;
  items: Array<{name: string}>;
}

interface RoomTypeProps {
  name: string;
  ref: string;
}

interface RoomTypeFilterProps {
  name: string;
  roomTypes: Array<RoomTypeProps>;
}

interface CreatedByFiltersProps {
  id: number;
  name: string;
}

interface BookingTrackerProps {
  bookingsForBookingTracker: Array<BookingsForBookingTrackerProps>;
  floors: Array<string>;
  bookingTypes: Array<BookingTypesProps>;
  bookingListFilters: {
    bookingStatusFilters: Array<BookingStatusFilterProps>;
    roomTypeFilters: Array<RoomTypeFilterProps>;
    createdByFilters: Array<CreatedByFiltersProps>;
  };
  rooms: Array<RoomsProps>;
  fetchBookingsForBookingTracker: (string) => any;
  fetchFloors: (string) => any;
  fetchBookingTypes: (string) => any;
  fetchAllRooms: (string) => any;
  fetchBookingListFilters: (string) => any;
  composeActions: (any) => void;
  openModal?: any;
  bookingNotesModal: boolean;
  requiresPermission: any;
  isLoading: boolean;
}

class BookingTracker extends React.Component<
  Container &
    LoginRequiredProps &
    WithFiltersProps<typeof initialFilters> &
    WithCursorPaginationProps &
    BookingTrackerProps
> {
  componentDidMount() {
    const {locationRef} = this.props.match.params;

    const newFiltersObj = _.omit(BOOKING_STATUSES, ['CANCELLED']);
    const newFiltersArr = _.values(newFiltersObj);
    this.setFilter('bookingStatuses', newFiltersArr);
    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING_TRACKER, locationRef},
      () => {
        this.props.fetchBookingsForBookingTracker({locationRef});
        this.props.fetchFloors({locationRef});
        this.props.fetchAllRooms({locationRef});
        this.props.fetchBookingTypes({locationRef});
        this.props.fetchBookingListFilters({locationRef});
      }
    );
  }

  setFilter = (field, value) => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.setFilter(field, value),
      () => this.props.onPaginationChange({pageSize: Math.abs(pageSize)}),
      () => fetchBookingsForBookingTracker({locationRef})
    ]);
  };

  clearFilters = () => {
    const {locationRef} = this.props.match.params;

    this.props.composeActions([
      () => this.props.clearFilters(),
      () => fetchBookingsForBookingTracker({locationRef})
    ]);
  };

  redirectToBookingDetail = ({bookingId}) => {
    const {locationRef} = this.props.match.params;

    window.open(reverse(BOOKING_DETAIL_URL, {bookingId, locationRef}));
  };

  openBookingNotesModal = ({bookingId}: any) => {
    const {locationRef} = this.props.match.params;
    this.props.openModal(MODALS.BOOKING_NOTES, {
      bookingId,
      locationRef,
      refetchExtraAction: () => [
        () => fetchBookingsForBookingTracker({locationRef})
      ]
    });
  };

  onPageChange = ({pageSize, cursor}) => {
    const {locationRef} = this.props.match.params;

    this.props.onPaginationChange({pageSize, cursor}, [
      () => fetchBookingsForBookingTracker({locationRef})
    ]);
  };

  render() {
    const {locationRef} = this.props.match.params;
    const bookings = this.props.bookingsForBookingTracker;
    const {
      bookingTypes,
      rooms,
      floors,
      bookingListFilters,
      bookingNotesModal,
      isLoading
    } = this.props;

    return (
      <Page activeTab="bookingTracker" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_BOOKING_TRACKER}
          locationRef={locationRef}>
          <ListContent
            className="BookingTracker"
            filters={this.props.getFilters()}
            setFilter={this.setFilter}
            initialFilters={initialFilters}
            navigationExtraElement={SubNavigation}>
            <ListContent.Content>
              {isLoading && <Loading />}
              {!isLoading && (
                <BookingsTable
                  bookings={bookings}
                  pagination={this.props.getPagination()}
                  onPageChange={this.onPageChange}
                  redirectToBookingDetail={this.redirectToBookingDetail}
                  openBookingNotesModal={this.openBookingNotesModal}
                />
              )}
            </ListContent.Content>
            <ListContent.Filters>
              <BookingsTrackerFilters
                locationRef={locationRef}
                getFilters={this.props.getFilters}
                setFilter={this.setFilter}
                clearFilters={this.clearFilters}
                bookingTypes={bookingTypes}
                floors={floors}
                rooms={rooms}
                bookingListFilters={bookingListFilters}
              />
            </ListContent.Filters>
          </ListContent>
          {bookingNotesModal && <BookingNotesModal />}
        </VisibleIf>
      </Page>
    );
  }
}

const mapDispatchToProps = {
  fetchBookingsForBookingTracker,
  fetchFloors,
  fetchBookingTypes,
  fetchAllRooms,
  fetchBookingListFilters,
  composeActions,
  requiresPermission,
  openModal
};

const mapStateToProps = state => ({
  bookingsForBookingTracker: selectBookingsForBookingTracker(state),
  isLoading: selectBookingsForBookingTrackerLoading(state),

  floors: selectFloors(state),
  bookingTypes: selectBookingTypes(state),
  bookingListFilters: selectBookingsFilters(state),
  rooms: selectAllRooms(state),
  bookingNotesModal: isModalOpen(state, MODALS.BOOKING_NOTES)
});

const componentWithFilters = withFilters(
  LoginRequired(BookingTracker),
  FILTERS.BOOKING_TRACKER,
  {initialFilters, saveToUrl: true, clearFiltersOnUnmount: false}
);

const componentWithFiltersAndPagination = withCursorPagination(
  componentWithFilters,
  PAGINATION.BOOKING_TRACKER,
  {saveToUrl: true}
);

export default connect(mapStateToProps, mapDispatchToProps)(
  componentWithFiltersAndPagination
);
